import type { FunctionComponent } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { useState, useRef, useEffect } from 'preact/hooks';
import { StatusBadge } from '../StatusBadge/StatusBadge.js';
import { useTaskStats } from '../CloudpackProvider/useTaskStats.js';
import { useErrorEvents } from '../ErrorDialog/useErrorEvents.js';
import { usePageLoadTimeReporter } from '../../hooks/usePageLoadTimeReporter.js';
import type { TaskStats } from '@ms-cloudpack/api-server/browser';

const StatusDialog = lazy(() => import('../StatusDialog/StatusDialog.js'));
const ErrorDialog = lazy(() => import('../ErrorDialog/ErrorDialog.js'));

export const Overlay: FunctionComponent = () => {
  const pageLoadTimeReport = usePageLoadTimeReporter();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const stats = useTaskStats();
  const prevStatsRef = useRef<TaskStats | undefined>();
  const { unsupported } = useErrorEvents();
  // Note: index.tsx handles the 'never' case (doesn't render anything)
  const showOverlay = window.__cloudpack?.pageSessionContext?.showOverlay || 'always';

  // If we haven't set a specific state and we become idle,
  // pop open the details if there are errors.
  useEffect(() => {
    if (
      !isDialogOpen &&
      prevStatsRef.current?.status !== 'idle' &&
      stats.status === 'idle' &&
      (prevStatsRef.current?.totalErrors || 0) !== (stats.totalErrors || 0)
    ) {
      setIsDialogOpen(true);
    }
    prevStatsRef.current = stats;
  }, [stats, isDialogOpen]);

  const result = unsupported.length ? (
    <ErrorDialog unsupported={unsupported} />
  ) : stats.totalErrors || showOverlay !== 'errors-only' ? (
    <>
      {isDialogOpen && <StatusDialog onClose={() => setIsDialogOpen(false)} />}
      <StatusBadge onOpenDialog={() => setIsDialogOpen(true)} pageLoadTimeReport={pageLoadTimeReport} />
    </>
  ) : null;

  return <Suspense fallback={null}>{result}</Suspense>;
};
